import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { PublicPageLayout } from '../components/PublicPageLayout';

const PlanWrap = styled.div`
  max-width: 420px;
  margin: 0 auto ${({ theme }) => theme.spacing.xxl};
`;

const Card = styled.div<{ $highlight?: boolean }>`
  background-color: ${({ theme }) => theme.colors.surface};
  border: ${({ $highlight, theme }) =>
    $highlight ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  position: relative;
`;

const Badge = styled.span`
  position: absolute;
  top: -12px;
  left: ${({ theme }) => theme.spacing.xl};
  padding: 4px 12px;
  background: linear-gradient(135deg, #3B82F6, #8B5CF6);
  color: white;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  letter-spacing: 0.04em;
`;

const PlanName = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const Price = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: ${({ theme }) => theme.fontSize.xxxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};

  span { font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.colors.textMuted}; font-weight: ${({ theme }) => theme.fontWeight.normal}; }
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

const Features = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  flex: 1;
`;

const Feature = styled.li`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: flex-start;

  &::before {
    content: '✓';
    color: ${({ theme }) => theme.colors.success};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    flex-shrink: 0;
  }
`;

const CtaButton = styled(Link)`
  display: block;
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  text-align: center;
  text-decoration: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  font-size: ${({ theme }) => theme.fontSize.sm};
  transition: background-color 0.15s ease;
  &:hover { background-color: ${({ theme }) => theme.colors.primaryHover}; }
`;

const Note = styled.p`
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

export function PricingPage() {
  return (
    <PublicPageLayout
      title="Pricing"
      subtitle="One simple plan. Everything included — no credits, no add-ons, no API keys to bring."
    >
      <PlanWrap>
        <Card $highlight>
          <Badge>EVERYTHING INCLUDED</Badge>
          <PlanName>Coopen SMM</PlanName>
          <Price>USD 20<span>/mo</span></Price>
          <Description>One plan for freelancers, brands and agencies managing multiple clients.</Description>
          <Features>
            <Feature>Unlimited post & ad generation</Feature>
            <Feature>11 social networks (Instagram, Facebook, X, LinkedIn, TikTok and more)</Feature>
            <Feature>All AIs included — no need to bring your own API key</Feature>
            <Feature>AI-styled posters, Image to Image & gallery</Feature>
            <Feature>Advanced template editor + Novah AI Review</Feature>
            <Feature>Campaign manager: A/B rotation + CSV / GHL export</Feature>
            <Feature>Up to 5 collaborators included (they don't pay extra)</Feature>
            <Feature>Cancel anytime</Feature>
          </Features>
          <CtaButton to="/register">Start now</CtaButton>
        </Card>
      </PlanWrap>

      <Note>
        Secure payment via PayPal (card or balance). We're in beta — if you need a
        custom plan or have questions, write to us at{' '}
        <a href="mailto:coopenstudio@gmail.com">coopenstudio@gmail.com</a>.
      </Note>
    </PublicPageLayout>
  );
}
